import { authStore } from '@store/AuthStore'
import { isTokenExpiredPayload } from '@utils/apiHelpers'
import { i18nStore } from '@store/I18nStore'

/**
 * Access-token refresh for every API call (`fetch` with our Bearer header):
 *  - a stale token on the way out (restored page, long idle) is refreshed before the request;
 *  - 401 TOKEN_EXPIRED → one shared /api/auth/refresh, then the original request once more;
 *  - once the session is over (refresh said expired / logged out), requests still carrying that
 *    token get a local 401 — nothing goes to the network, no burst of red in the console.
 * The refresh itself (cookie-based) and the other /api/auth/* calls are left alone.
 */
const AUTH_ENDPOINT_RE = /\/api\/auth\/(login|refresh|logout|register|forgot-password|reset-password|validate-reset-token|accept-invite|verify-email|resend-verification)(\?|\/|$)/

let installed = false

/** The answer the backend would give to a dead token — callers handle it the same way. */
function sessionOverResponse(): Response {
  const message = i18nStore.language === 'ru' ? 'Сессия истекла, войдите заново' : 'Session expired, please sign in again'
  return new Response(JSON.stringify({ success: false, code: 'TOKEN_EXPIRED', message }), {
    status: 401,
    headers: { 'Content-Type': 'application/json' },
  })
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.toString()
  return input.url
}

function readBearer(input: RequestInfo | URL, init: RequestInit | undefined): string | null {
  let raw: string | null = null
  if (init?.headers) {
    raw = new Headers(init.headers).get('Authorization')
  } else if (typeof Request !== 'undefined' && input instanceof Request) {
    raw = input.headers.get('Authorization')
  }
  const m = /^Bearer\s+(.+)$/i.exec(String(raw ?? '').trim())
  return m ? m[1].trim() : null
}

function withBearer(input: RequestInfo | URL, init: RequestInit | undefined, token: string): RequestInit {
  const source = init?.headers ?? (typeof Request !== 'undefined' && input instanceof Request ? input.headers : undefined)
  const headers = new Headers(source)
  headers.set('Authorization', `Bearer ${token}`)
  return { ...(init ?? {}), headers }
}

/** A streamed body cannot be sent twice; everything else we use (string, FormData, Blob) can. */
function bodyReusable(init: RequestInit | undefined): boolean {
  const body = init?.body
  if (!body) return true
  return !(typeof ReadableStream !== 'undefined' && body instanceof ReadableStream)
}

async function isTokenExpiredResponse(response: Response): Promise<boolean> {
  try {
    const data = await response.clone().json()
    return isTokenExpiredPayload(data && typeof data === 'object' ? (data as Record<string, unknown>) : null)
  } catch {
    return false
  }
}

export function installAuthFetchInterceptor(): void {
  if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') return
  installed = true
  const nativeFetch = window.fetch.bind(window)

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = requestUrl(input)
    const bearer = readBearer(input, init)
    if (!bearer || !url.includes('/api/') || AUTH_ENDPOINT_RE.test(url)) return nativeFetch(input, init)
    if (authStore.isDeadToken(bearer)) return sessionOverResponse()
    if (!authStore.isOwnToken(bearer)) return nativeFetch(input, init)

    const retryInput = typeof Request !== 'undefined' && input instanceof Request ? input.clone() : input

    if (authStore.shouldRefreshBeforeRequest(bearer)) {
      const restored = await authStore.refreshAccessToken()
      if (restored === 'expired' || restored === 'blocked') return sessionOverResponse()
    }
    const current = authStore.accessToken
    const firstInit = current && current !== bearer ? withBearer(input, init, current) : init
    const response = await nativeFetch(input, firstInit)
    if (response.status !== 401 || !bodyReusable(init)) return response
    if (!(await isTokenExpiredResponse(response))) return response

    const sentWith = current && current !== bearer ? current : bearer
    // Token already rotated while this request was in flight → just resend with it;
    // otherwise one shared refresh, then the original request once more.
    const result = authStore.accessToken && authStore.accessToken !== sentWith ? 'ok' : await authStore.refreshAccessToken()
    const fresh = authStore.accessToken
    if (result !== 'ok' || !fresh) return response
    return nativeFetch(retryInput, withBearer(retryInput, init, fresh))
  }
}
